// ============================================================================
// ui/theatre.js — the "curtains open" moment + the wheel lock (WD 492, Ryan 2026-11-01)
// ============================================================================
// Ryan: *"you know how when you go to the movies ... the curtain extends to make the screen bigger"* - after "Start
// designing" on a big desktop screen, the navy band around the designer grows out to the screen edges, covering the
// site header and the white page below, so the only thing on the display is the designer window on navy.
//   And: *"you can't scroll up or down using the scroll wheel while your mouse is over any part of the designer ...
//   you need to remain locked in ... you can't scroll off it unless you move your mouse into the blue border area".*
//
// THE VEIL: four fixed navy panels AROUND the designer's frame (window + step pills) - above, below, left, right -
// never over it. ⛔ NOT ONE LAYER WITH A CLIP-PATH HOLE (the first build): the browser's fast wheel-scrolling picks its
// target on the compositor, which ignores clip-path, so a wheel over the side pane scrolled THE PAGE through the hole
// even though the event itself reported the pane (measured: pane 0 px, page +3400 px). Nothing covering the window =
// nothing to mis-route. The OUTER edge is what animates: from the navy band's own rectangle (so at t=0 the panels are
// invisible - same colour as the band) to the viewport. The designer is never moved or re-parented.
//   z 60: above the site header (z 30), below every designer dialog (scrim z 100), so the see-through editor, the
//   help chat and the photo card all sit over it whole.
//   ⛔ THE SITE HEADER IS HIDDEN (visibility) WHILE THE VEIL IS UP. It changes height with scroll direction (78 px
//   scrolling down, ~220 px when a scroll-up reveals the full menu), so any centring that measured it was measuring a
//   moving target - and where it overlapped the frame it would show THROUGH the hole, over the window. Hidden, the
//   window simply centres in the screen. It is under the navy anyway, so nobody sees it go.
//   ⛔ EXIT = THE BUYER SCROLLING THE PAGE (Ryan's pick: "Navy slides back, returns on click"), read from what the
//   buyer DID, never from a scroll event: a wheel over the navy (outside the window), a page-scroll key outside the
//   designer, or a press on the page scrollbar. A scroll EVENT alone also fires when the window grows after the
//   hand-off (the 3D lands ~5 s later, 648 -> 790 px) and the browser re-anchors the page - reading that as the buyer
//   leaving pulled the navy back on its own, on 3 of 4 screen sizes. Those scrolls only keep the hole aligned, and
//   the window re-centres once it has settled. The outer edge pulls back to the band, tracked LIVE every frame because
//   the band is moving. A click inside the window, while the whole frame is on screen, grows it back.
//   ⛔ ONLY WHERE IT FITS: desktop storefront, not a phone (already full screen), not the portal, not a shared link
//   (its own viewport lock), and only when the whole frame fits on the screen.
// THE WHEEL LOCK is separate and always on (every screen, storefront and portal): over the window, a wheel event the
// window cannot use (no inner scroller can move that way) is cancelled, so it never scrolls the page.
// ============================================================================

const NAVY = 'rgb(0, 15, 28)'; // the embed band's own colour (#weight-designer-root / .wd-embed-shell)
const GROW_MS = 650;
const EDGE = 16; // the least navy above and below the frame

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/** Wheel lock: a wheel over the designer window never reaches the page. */
export function installWheelLock(root) {
  if (!root || root.__wdWheelLock) return;
  root.__wdWheelLock = true;
  root.addEventListener('wheel', (e) => {
    const win = e.target && e.target.closest && e.target.closest('.wd-stage-region');
    if (!win || !root.contains(win)) return; // the navy border (and the step pills) scroll the page as normal
    // an inner scroller that can still move this way gets the wheel (the pane, a carousel, a dialog body)
    for (let el = e.target; el && el !== win.parentElement; el = el.parentElement) {
      if (el.nodeType !== 1) continue;
      const cs = getComputedStyle(el);
      if (e.deltaY && /(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) {
        if (e.deltaY > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0) return;
      }
      if (e.deltaX && /(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 1) {
        if (e.deltaX > 0 ? el.scrollLeft + el.clientWidth < el.scrollWidth - 1 : el.scrollLeft > 0) return;
      }
    }
    e.preventDefault(); // nothing inside can use it: it must not scroll the page (the 3D zoom still sees the event)
  }, { passive: false });
}

/** The site's sticky/fixed header element at the top of the screen, or null. */
function findHeader(root) {
  let el = document.elementFromPoint(Math.round(innerWidth / 2), 2);
  for (; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
    if (root.contains(el)) return null;
    const pos = getComputedStyle(el).position;
    if (pos === 'fixed' || pos === 'sticky') return el;
  }
  return null;
}

export function createTheatre({ root, enabled = () => true }) {
  const frameEl = () => root.querySelector('.wd-frame') || root;
  const bandEl = () => root.closest('.wd-embed-shell') || root;
  let veil = null, raf = 0, state = 'off', progress = 0, restY = 0, selfScroll = false, header = null, headerVis = '';
  let armed = false; // the click-back works only once the designer has started (the gate's own clicks are inside the window too)
  const reduce = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  // a short trail of what the theatre did and why, for the probes (tools/scratch/verify-492-theatre.mjs)
  const log = (m) => { try { const L = (window.__wdTheatreLog = window.__wdTheatreLog || []); L.push(`${Math.round(performance.now())} ${m} y=${Math.round(window.scrollY)}`); if (L.length > 60) L.shift(); } catch {} };
  // the window settles after the hand-off (the 3D lands later and the frame grows): the hole follows it, then re-centres
  let settleTimer = 0;
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
    if (state === 'off' || state === 'leaving') return;
    if (!raf) paint(progress);
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      if (state !== 'on' && state !== 'growing') return;
      if (!fits()) { log('no longer fits'); exit(); return; }
      if (state === 'on') recentre();
    }, 250);
  }) : null;

  const fits = () => frameEl().getBoundingClientRect().height + EDGE * 2 <= innerHeight;
  function paint(p) {
    if (!veil) return;
    const W = innerWidth, H = innerHeight;
    const b = bandEl().getBoundingClientRect(), f = frameEl().getBoundingClientRect();
    const k = 1 - p; // 1 = outer edge at the band, 0 = outer edge at the screen
    const top = Math.min(H, Math.max(0, b.top)) * k, bot = H - Math.min(H, Math.max(0, H - b.bottom)) * k;
    const ft = Math.max(top, Math.min(bot, f.top)), fb = Math.max(ft, Math.min(bot, f.bottom));
    const fl = Math.max(0, Math.min(W, f.left)), fr = Math.max(fl, Math.min(W, f.right));
    const place = (el, x, y, w, h) => { el.style.cssText = `left:${x}px;top:${y}px;width:${Math.max(0, w)}px;height:${Math.max(0, h)}px;background:${NAVY}`; };
    const [pt, pb, pl, pr] = veil;
    place(pt, 0, top, W, ft - top);       // above the frame
    place(pb, 0, fb, W, bot - fb);        // below it
    place(pl, 0, ft, fl, fb - ft);        // left of it
    place(pr, fr, ft, W - fr, fb - ft);   // right of it
  }
  function animate(to, done) {
    cancelAnimationFrame(raf);
    const from = progress, t0 = performance.now(), ms = reduce() ? 0 : GROW_MS * Math.abs(to - from);
    const step = (now) => {
      const t = ms ? Math.min(1, (now - t0) / ms) : 1;
      progress = from + (to - from) * ease(t);
      paint(progress);
      if (t < 1) raf = requestAnimationFrame(step); else { raf = 0; done && done(); }
    };
    raf = requestAnimationFrame(step);
  }
  /** Our own scroll to put the frame in the middle of the screen. Marked, so it never reads as the buyer leaving. */
  function scrollToCentre() {
    return new Promise((resolve) => {
      const f = frameEl().getBoundingClientRect();
      const dy = f.top - Math.max(EDGE, (innerHeight - f.height) / 2);
      log(`centre: frame top ${Math.round(f.top)} h ${Math.round(f.height)} -> dy ${Math.round(dy)}`);
      if (Math.abs(dy) < 2) { restY = window.scrollY; resolve(); return; }
      selfScroll = true;
      const target = Math.max(0, window.scrollY + dy);
      window.scrollTo({ top: target, behavior: reduce() ? 'auto' : 'smooth' });
      /* ⛔ NOT `scrollend`: it can be the END OF AN EARLIER SCROLL (the click that pressed Start designing), which
         released `selfScroll` while this glide was still moving - and the glide then read as the buyer leaving.
         Done = the page has stopped moving (3 frames unchanged) or 1.2 s, whichever first. */
      const t0 = performance.now();
      let last = -1, still = 0;
      const watch = () => {
        const y = window.scrollY;
        still = Math.abs(y - last) < 0.5 ? still + 1 : 0;
        last = y;
        if ((still >= 3 && Math.abs(y - target) < 2) || still >= 12 || performance.now() - t0 > 1200) {
          setTimeout(() => { selfScroll = false; restY = window.scrollY; resolve(); }, 30); // the last scroll event lands first
          return;
        }
        requestAnimationFrame(watch);
      };
      requestAnimationFrame(watch);
    });
  }
  // a scroll EVENT never ends the theatre (see the header); it only keeps the hole on the window
  function onScroll() { if (!raf) paint(progress); }
  const live = () => state === 'on' || state === 'growing';
  function onWheel(e) {
    if (!live() || selfScroll) return;
    if (e.target && e.target.closest && e.target.closest('.wd-stage-region')) return; // inside the window: the lock's
    exit(); // the wheel itself goes on to scroll the page
  }
  const SCROLL_KEYS = new Set(['PageDown', 'PageUp', 'Home', 'End', ' ', 'ArrowDown', 'ArrowUp']);
  function onKey(e) {
    if (!live() || !SCROLL_KEYS.has(e.key)) return;
    const t = e.target;
    if (t && (root.contains(t) || t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    exit();
  }
  function onPointer(e) {
    // a press on the page's own scrollbar lands on <html> outside the content box
    if (live() && (e.target === document.documentElement) && e.clientX >= document.documentElement.clientWidth) exit();
  }
  function onResize() {
    if (state !== 'on') return;
    if (!fits()) exit(); else { paint(1); recentre(); }
  }
  async function recentre() {
    if (state !== 'on' || selfScroll) return;
    await scrollToCentre();
    if (state === 'on') paint(1);
  }
  function hideHeader(on) {
    if (on) {
      header = findHeader(root);
      if (header) { headerVis = header.style.visibility; header.style.visibility = 'hidden'; }
    } else if (header) {
      header.style.visibility = headerVis; header = null;
    }
  }

  async function enter({ start = false } = {}) {
    if (start) armed = true;
    if (state !== 'off' || !enabled() || !fits()) { log(`enter refused (state ${state}, fits ${fits()})`); return; }
    state = 'centring'; log('enter');
    hideHeader(true);
    if (!veil) {
      veil = [0, 1, 2, 3].map(() => {
        const d = document.createElement('div');
        d.className = 'wd-theatre-veil';
        d.setAttribute('aria-hidden', 'true');
        return d;
      });
    }
    progress = 0;
    veil.forEach((d) => document.body.appendChild(d));
    paint(0);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('wheel', onWheel, { passive: true, capture: true });
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, true);
    /* ⛔ THE ROOT, NOT `.wd-frame`: at the hand-off the gate's frame has just been removed and the designer's may not
       exist yet, so the frame lookup can land on a stand-in that never resizes - the window then grew 648 -> 790 px
       and was never re-centred. The root always exists and resizes with the window. */
    if (ro) ro.observe(root);
    /* THE GROW AND THE GLIDE RUN TOGETHER: the curtain opens on the click, not after the centring. Right after the
       hand-off the 3D boot ties up the page, and waiting for the glide first delayed the grow by up to 4 s on a slow
       machine. `paint` reads both rectangles live every frame, so the hole rides the window through the glide. */
    state = 'growing'; log('growing');
    animate(1, () => { if (state === 'growing') { state = 'on'; log('on'); recentre(); } });
    scrollToCentre();
  }
  function exit() {
    if (state === 'off' || state === 'leaving') return;
    log(`exit from ${state} (restY ${restY})`);
    state = 'leaving';
    if (ro) ro.disconnect();
    window.removeEventListener('wheel', onWheel, { capture: true });
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('pointerdown', onPointer, true);
    animate(0, () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (veil) veil.forEach((d) => d.remove());
      hideHeader(false);
      state = 'off';
    });
  }
  // the way back: a click inside the window while the whole frame is on screen (Ryan's pick)
  root.addEventListener('pointerdown', (e) => {
    if (!armed || state !== 'off' || !enabled()) return;
    if (!(e.target && e.target.closest && e.target.closest('.wd-stage-region'))) return;
    const f = frameEl().getBoundingClientRect();
    if (f.top < 0 || f.bottom > innerHeight) return;
    enter();
  });
  try { Object.defineProperty(window, '__wdTheatreState', { configurable: true, get: () => `${state} p=${progress.toFixed(2)}` }); } catch {}
  return { enter, exit, isOn: () => state === 'on' };
}
